import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Gift,  
  Copy, 
  Check,
  AlertCircle,
  Users,
  Clock,
  Infinity,
  Tag,
  Coins
} from 'lucide-react';
import { useUser } from '../context/UserContext';
import { giftCardManager } from '../utils/giftCardManager';
import { GiftCardOrder } from '../types';
import toast from 'react-hot-toast';
import {
  GIFT_CARD_BULK_DISCOUNT_RATE,
  GIFT_CARD_BULK_DISCOUNT_THRESHOLD,
  getPremiumPlanPrice,
} from '../config/pricing';

// Declare atlos for TypeScript
declare global {
  interface Window {
    atlos: {
      Pay: (params: {
        merchantId: string;
        orderId: string;
        orderAmount: number;
        orderCurrency?: string;
        orderDescription?: string;
        successUrl?: string;
        cancelUrl?: string;
        theme?: 'light' | 'dark';
        recurring?: boolean;
        recurringInterval?: 'monthly' | 'yearly';
      }) => void;
    };
  }
  
  // eslint-disable-next-line no-var
  var atlos: Window['atlos'];
}

type GiftCardPlan = 'monthly' | 'yearly' | 'lifetime';

interface GiftCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  completedOrderId?: string | null;
}

type ModalStep = 'select' | 'purchase' | 'redeem' | 'payment' | 'success';

const GiftCardModal: React.FC<GiftCardModalProps> = ({ isOpen, onClose, completedOrderId }) => {
  const { state: userState, updatePremiumStatus } = useUser();
  const [currentStep, setCurrentStep] = useState<ModalStep>('select');
  const [selectedPlan, setSelectedPlan] = useState<GiftCardPlan>('monthly');
  const [quantity, setQuantity] = useState(1);
  const [redeemCode, setRedeemCode] = useState('');
  const [currentOrder, setCurrentOrder] = useState<GiftCardOrder | null>(null);
  const [copiedCodes, setCopiedCodes] = useState<Set<string>>(new Set());
  const [isProcessing, setIsProcessing] = useState(false);

  const planDetails: Record<GiftCardPlan, {
    name: string;
    price: number;
    duration: string;
    icon: React.ElementType;
    color: string;
    bgColor: string;
    savings?: string;
  }> = {
    monthly: {
      name: 'Monthly Premium',
      price: getPremiumPlanPrice('monthly'),
      duration: '1 month',
      icon: Clock,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10'
    },
    yearly: {
      name: 'Yearly Premium',
      price: getPremiumPlanPrice('yearly'),
      duration: '12 months',
      icon: Users,
      color: 'text-green-500',
      bgColor: 'bg-green-500/10',
      savings: '20% off'
    },
    lifetime: {
      name: 'Lifetime Premium',
      price: getPremiumPlanPrice('lifetime'),
      duration: 'Forever',
      icon: Infinity,
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10'
    }
  };

  const totalAmount = planDetails[selectedPlan].price * quantity;
  const bulkDiscount = quantity >= GIFT_CARD_BULK_DISCOUNT_THRESHOLD ? GIFT_CARD_BULK_DISCOUNT_RATE : 0;
  const discountAmount = totalAmount * bulkDiscount;
  const finalAmount = totalAmount - discountAmount;

  const handlePurchase = () => {
    if (!userState.isAuthenticated || !userState.user) {
      toast.error('Please log in to purchase gift cards');
      return;
    }

    if (!globalThis.atlos) {
      toast.error('Payment system not loaded yet. Please try again in a moment.');
      return;
    }

    setIsProcessing(true);
    
    // Create order
    const order = giftCardManager.createOrder(userState.user.id, {
      planType: selectedPlan,
      quantity,
      totalAmount: finalAmount // Use discounted amount
    });

    sessionStorage.setItem('typeRush_pendingGiftOrderId', order.id);

    setCurrentOrder(order);
    
    // Initiate Atlos payment
    const orderId = `giftcard_${selectedPlan}_${quantity}_${Date.now()}`;
    
    try {
      globalThis.atlos.Pay({
        merchantId: process.env.REACT_APP_ATLOS_MERCHANT_ID || 'GT8LTIFOMN',
        orderId: orderId,
        orderAmount: finalAmount,
        orderCurrency: 'USD',
        orderDescription: `TypeRush Gift Cards - ${quantity}x ${planDetails[selectedPlan].name}${bulkDiscount > 0 ? ' (10% Bulk Discount)' : ''}`,
        theme: 'dark',
        successUrl: `${globalThis.location.origin}/premium?giftcard_success=true&order_id=${order.id}`,
        cancelUrl: `${globalThis.location.origin}/premium?giftcard_cancelled=true`
      });

      toast.success('Opening secure payment window...');
      setIsProcessing(false);
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Failed to open payment window. Please try again.');
      setIsProcessing(false);
    }
  };

  const handleRedeem = () => {
    if (!userState.isAuthenticated || !userState.user) {
      toast.error('Please log in to redeem gift cards');
      return;
    }

    if (!giftCardManager.validateCodeFormat(redeemCode)) {
      toast.error('Invalid gift card code format. Please use XXXX-XXXX-XXXX format.');
      return;
    }

    setIsProcessing(true);
    
    const result = giftCardManager.redeemGiftCard(redeemCode, userState.user.id);
    
    if (result.success && result.giftCard) {
      // Update user's premium status
      updatePremiumStatus(result.giftCard.planType);
      
      toast.success(result.message);
      onClose();
    } else {
      toast.error(result.message);
    }
    
    setIsProcessing(false);
  };

  const removeCopiedCode = (code: string) => {
    setCopiedCodes(prev => {
      const newSet = new Set(Array.from(prev));
      newSet.delete(code);
      return newSet;
    });
  };

  const copyToClipboard = (code: string) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopiedCodes(prev => new Set(Array.from(prev).concat(code)));
      toast.success('Gift card code copied to clipboard!');
      setTimeout(() => removeCopiedCode(code), 2000);
    });
  };

  const resetModal = () => {
    setCurrentStep('select');
    setSelectedPlan('monthly');
    setQuantity(1);
    setRedeemCode('');
    setCurrentOrder(null);
    setCopiedCodes(new Set());
    setIsProcessing(false);
  };

  const handleClose = () => {
    resetModal();
    sessionStorage.removeItem('typeRush_pendingGiftOrderId');
    onClose();
  };

  useEffect(() => {
    if (!isOpen || !completedOrderId) return;

    const pendingOrderId = sessionStorage.getItem('typeRush_pendingGiftOrderId');
    if (!pendingOrderId || pendingOrderId !== completedOrderId) {
      toast.error('Payment confirmation could not be verified for this session.');
      return;
    }

    const completedOrder = giftCardManager.completeOrder(completedOrderId);
    if (!completedOrder) {
      toast.error('Unable to load your completed order.');
      return;
    }

    setCurrentOrder(completedOrder);
    setCurrentStep('success');
    sessionStorage.removeItem('typeRush_pendingGiftOrderId');
  }, [isOpen, completedOrderId]);

  const renderStepContent = () => {
    switch (currentStep) {
      case 'select':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <Gift className="w-12 h-12 text-accent mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-2">Gift Cards & Premium Codes</h2>
              <p className="text-textMuted">Purchase gift cards or redeem premium codes</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setCurrentStep('purchase')}
                className="p-6 border border-secondary rounded-xl bg-cardBg hover:bg-secondary transition-all"
              >
                <Coins className="w-8 h-8 text-accent mx-auto mb-3" />
                <h3 className="font-semibold mb-2">Purchase Gift Cards</h3>
                <p className="text-sm text-textMuted">Buy gift cards with crypto via Atlos</p>
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setCurrentStep('redeem')}
                className="p-6 border border-secondary rounded-xl bg-cardBg hover:bg-secondary transition-all"
              >
                <Gift className="w-8 h-8 text-green-500 mx-auto mb-3" />
                <h3 className="font-semibold mb-2">Redeem Code</h3>
                <p className="text-sm text-textMuted">Enter your premium gift card code</p>
              </motion.button>
            </div>
          </div>
        );

      case 'purchase':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Coins className="w-6 h-6 text-accent" />
                <h2 className="text-xl font-bold">Purchase Gift Cards</h2>
              </div>
              <button 
                onClick={() => setCurrentStep('select')} 
                className="text-textMuted hover:text-text"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Plan Selection */}
            <div className="space-y-3">
              <fieldset className="space-y-3">
                <legend className="block text-sm font-medium">Select Plan Type</legend>
                {(Object.keys(planDetails) as Array<keyof typeof planDetails>).map((plan) => {
                  const details = planDetails[plan];
                  const Icon = details.icon;
                  return (
                    <motion.button
                      key={plan}
                      whileHover={{ scale: 1.01 }}
                      onClick={() => setSelectedPlan(plan)}
                      className={`w-full p-4 border rounded-lg transition-all ${
                        selectedPlan === plan
                          ? 'border-accent bg-accent/10'
                          : 'border-secondary bg-cardBg hover:bg-secondary'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className={`p-2 rounded-lg ${details.bgColor}`}>
                            <Icon className={`w-5 h-5 ${details.color}`} />
                          </div>
                          <div className="text-left">
                            <div className="font-medium">{details.name}</div>
                            <div className="text-sm text-textMuted">{details.duration}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold">${details.price}</div>
                          {'savings' in details && details.savings && (
                            <div className="text-xs text-green-500">{details.savings}</div>
                          )}
                        </div>
                      </div>
                    </motion.button>
                  );
                })}
              </fieldset>
            </div>

            {/* Quantity Selection */}
            <div className="space-y-3">
              <label htmlFor="quantity-input" className="block text-sm font-medium">Quantity</label>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-10 h-10 border border-secondary rounded-lg bg-cardBg hover:bg-secondary transition-all flex items-center justify-center"
                >
                  -
                </button>
                <input
                  id="quantity-input"
                  type="number"
                  min="1"
                  max="50"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, Math.min(50, Number.parseInt(e.target.value, 10) || 1)))}
                  className="w-20 text-center bg-cardBg border border-secondary rounded-lg p-2 focus:outline-none focus:ring-1 focus:ring-accent"
                />
                <button
                  onClick={() => setQuantity(Math.min(50, quantity + 1))}
                  className="w-10 h-10 border border-secondary rounded-lg bg-cardBg hover:bg-secondary transition-all flex items-center justify-center"
                >
                  +
                </button>
                <span className="text-sm text-textMuted ml-2">Max 50 per purchase</span>
              </div>
              
              {/* Bulk Discount Notice */}
              {quantity >= 10 && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg"
                >
                  <div className="flex items-center space-x-2">
                    <Tag className="w-4 h-4 text-green-500" />
                    <span className="text-sm font-medium text-green-500">
                      Bulk Discount Applied! 10% off for 10+ gift cards
                    </span>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Total */}
            <div className="p-4 bg-cardBg border border-secondary rounded-lg">
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span>Subtotal ({quantity} cards)</span>
                  <span>${totalAmount.toFixed(2)}</span>
                </div>
                
                {bulkDiscount > 0 && (
                  <div className="flex justify-between items-center text-green-500">
                    <span>Bulk Discount (10%)</span>
                    <span>-${discountAmount.toFixed(2)}</span>
                  </div>
                )}
                
                <div className="border-t border-secondary pt-2 flex justify-between items-center">
                  <span className="font-medium">Total Amount</span>
                  <span className="text-2xl font-bold text-accent">${finalAmount.toFixed(2)}</span>
                </div>
              </div>
              <div className="text-sm text-textMuted mt-1">
                {quantity} × {planDetails[selectedPlan].name} gift card{quantity > 1 ? 's' : ''}
                {bulkDiscount > 0 && ' with bulk discount'}
              </div>
            </div>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handlePurchase}
              disabled={isProcessing}
              className="w-full bg-accent hover:bg-yellow-500 text-primary font-semibold py-3 rounded-lg transition-all duration-200 disabled:opacity-50"
            >
              {(() => {
                if (isProcessing) return 'Opening Payment...';
                const cardLabel = quantity > 1 ? 's' : '';
                return `Purchase ${quantity} Gift Card${cardLabel} - $${finalAmount.toFixed(2)}`;
              })()}
            </motion.button>
          </div>
        );

      case 'redeem':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Gift className="w-6 h-6 text-green-500" />
                <h2 className="text-xl font-bold">Redeem Gift Card</h2>
              </div>
              <button 
                onClick={() => setCurrentStep('select')} 
                className="text-textMuted hover:text-text"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="text-center py-4">
              <Gift className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <p className="text-textMuted mb-6">
                Enter your gift card code to activate premium features
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label htmlFor="gift-card-code" className="block text-sm font-medium mb-2">Gift Card Code</label>
                <input
                  id="gift-card-code"
                  type="text"
                  placeholder="XXXX-XXXX-XXXX"
                  value={redeemCode}
                  onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
                  className="w-full bg-cardBg border border-secondary rounded-lg p-3 focus:outline-none focus:ring-1 focus:ring-accent text-center font-mono text-lg tracking-wider"
                  maxLength={14}
                />
                <p className="text-xs text-textMuted mt-1 text-center">
                  Enter the 12-character code in format XXXX-XXXX-XXXX
                </p>
              </div>

              <div className="p-4 bg-cardBg border border-secondary rounded-lg">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="font-medium mb-1">Important Notes:</p>
                    <ul className="text-textMuted space-y-1">
                      <li>• Each code can only be used once</li>
                      <li>• You cannot redeem your own gift cards</li>
                      <li>• Gift cards expire after 1 year</li>
                      <li>• Premium access starts immediately upon redemption</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleRedeem}
              disabled={isProcessing || !redeemCode || !giftCardManager.validateCodeFormat(redeemCode)}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 rounded-lg transition-all duration-200 disabled:opacity-50"
            >
              {isProcessing ? 'Redeeming...' : 'Redeem Gift Card'}
            </motion.button>
          </div>
        );

      case 'success':
        return (
          <div className="space-y-6 text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto"
            >
              <Check className="w-10 h-10 text-green-500" />
            </motion.div>

            <div>
              <h2 className="text-2xl font-bold text-green-500 mb-2">Purchase Successful!</h2>
              <p className="text-textMuted">Your gift cards have been generated and are ready to share.</p>
            </div>

            {currentOrder && (
              <div className="space-y-4">
                <div className="p-4 bg-cardBg border border-secondary rounded-lg">
                  <h3 className="font-semibold mb-3">Your Gift Card Codes</h3>
                  <div className="space-y-3">
                    {currentOrder.giftCards.map((giftCard, index) => (
                      <div key={giftCard.id} className="flex items-center justify-between p-3 bg-secondary rounded-lg">
                        <div className="flex-1">
                          <div className="font-mono text-lg font-bold">{giftCard.code}</div>
                          <div className="text-sm text-textMuted">
                            {planDetails[giftCard.planType as 'monthly' | 'yearly' | 'lifetime'].name} - ${giftCard.value}
                          </div>
                        </div>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => copyToClipboard(giftCard.code)}
                          className="ml-3 p-2 bg-accent hover:bg-yellow-500 text-primary rounded-lg transition-all"
                        >
                          {copiedCodes.has(giftCard.code) ? (
                            <Check className="w-4 h-4" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </motion.button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                    <div className="text-sm">
                      <p className="font-medium mb-1">Share with Friends:</p>
                      <p className="text-textMuted">
                        Send these codes to friends so they can redeem premium access. 
                        Each code can only be used once and expires in 1 year.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleClose}
              className="w-full bg-accent hover:bg-yellow-500 text-primary font-semibold py-3 rounded-lg transition-all duration-200"
            >
              Close
            </motion.button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            className="bg-primary border border-secondary rounded-xl p-6 w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {currentStep !== 'success' && currentStep !== 'purchase' && currentStep !== 'redeem' && (
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center space-x-2">
                  <Gift className="w-6 h-6 text-accent" />
                  <h1 className="text-xl font-bold">Gift Cards</h1>
                </div>
                <button onClick={handleClose} className="text-textMuted hover:text-text">
                  <X className="w-5 h-5" />
                </button>
              </div>
            )}

            {renderStepContent()}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default GiftCardModal;
